<!-- @format -->

<template>
	<el-container class="pform-main-container">
		<el-aside class="field-container">
			<left-aside
				:can-drag="canDrag"
				:name="name"
				:can-template="canTemplate"
				:template-list="templateList"
				@on-template-change="emit('on-template-change', $event)"
			>
				<template #default>
					<slot name="left-aside" />
				</template>
			</left-aside>
		</el-aside>

		<el-container class="center-container">
			<el-header class="center-header">
				<widget-toolbar
					ref="toolbarRef"
					:other-func="otherFunc"
					:name="name"
					:is-mobile="isMobile"
					@on-save="(...args) => emit('on-save', ...args)"
				>
					<template #toolbar-save="item">
						<slot name="toolbar-save" v-bind="item" />
					</template>
					<template #toolbar-preview="item">
						<slot name="toolbar-preview" v-bind="item" />
					</template>
					<template #toolbar-right>
						<slot name="toolbar-right" />
					</template>
					<template #preview-top>
						<slot name="preview-top" />
					</template>
					<template #preview-bottom>
						<slot name="preview-bottom" />
					</template>
				</widget-toolbar>
			</el-header>
			<el-main class="center-main">
				<widget-form
					ref="widgetFormRef"
					:default-widget-datas="defaultWidgetDatas"
					:default-form-attribute="defaultFormAttribute"
					:is-field-to-container="isFieldToContainer"
					:is-container-to-container="isContainerToContainer"
					:can-delete="canDelete"
					:can-sort="canSort"
					:can-drag-in="canDragIn"
					:name="name"
					:init-code="initCode"
					:is-mobile="isMobile"
					@on-delete="emit('on-delete', $event)"
				/>
			</el-main>
		</el-container>

		<el-aside class="setting-container">
			<right-aside
				:name="name"
				:other-func="otherFunc"
				:can-form-attribute="canFormAttribute"
				:can-change-field="canChangeField"
				@on-attribute-change="emit('on-attribute-change', $event)"
			/>
		</el-aside>
	</el-container>
</template>

<script setup lang="ts">
	import { useBaseConfigStoreWithOut } from '../config/widget';
	import { useDragStoreWithOut } from '../store';
	import LeftAside from './left-aside/index.vue';
	import WidgetToolbar from './widget-toolbar/index.vue';
	import WidgetForm from './widget-form/index.vue';
	import RightAside from './right-aside/index.vue';
	import type { SetBaseConfig, FieldsType, ScriptFuncType, TemplateType } from '../types';

	interface Props {
		setBaseConfig?: SetBaseConfig;
		otherFunc?: ScriptFuncType;
		isFieldToContainer?: boolean;
		isContainerToContainer?: boolean;
		defaultWidgetDatas?: FieldsType[];
		defaultFormAttribute?: Record<string, any>;
		canDelete?: boolean | ((a: any) => boolean);
		canSort?: boolean | ((a: any) => boolean);
		canDragIn?: boolean | ((a: any) => boolean);
		canChangeField?: boolean | ((a: any) => boolean);
		canDrag?: boolean;
		name?: string;
		canTemplate?: boolean;
		templateList?: TemplateType[];
		initCode?: boolean | ((a: any) => string);
		canFormAttribute?: boolean;
		isMobile?: boolean;
	}

	const props = withDefaults(defineProps<Props>(), {
		canDelete: true,
		canSort: true,
		canDrag: true,
		canDragIn: true,
		canChangeField: true,
		initCode: true,
		canFormAttribute: true,
		isContainerToContainer: true,
	});

	const emit = defineEmits(['on-delete', 'on-attribute-change', 'on-save', 'on-template-change']);

	const toolbarRef = ref<InstanceType<typeof WidgetToolbar> | null>(null);
	const widgetFormRef = ref<InstanceType<typeof WidgetForm> | null>(null);

	const validateFieldAttribute = computed(() => toolbarRef.value?.validateFieldAttribute);

	const widgetDatas = computed(() => widgetFormRef.value?.widgetDatas);
	const formAttribute = computed(() => widgetFormRef.value?.formAttribute);

	const dragStore = useDragStoreWithOut(props.name);

	onBeforeMount(() => {
		const baseConfigStore = useBaseConfigStoreWithOut(props.name);
		baseConfigStore.$reset();
		dragStore.$reset();
		props.setBaseConfig && baseConfigStore.setState(props.setBaseConfig);
	});

	//表单配置组件回显数据
	provide('dragStore', dragStore);

	defineExpose({ validateFieldAttribute, widgetDatas, formAttribute, dragStore });
</script>

<style scoped lang="scss">
	.pform-main-container {
		height: 100%;

		.field-container {
			width: 220px !important;
			padding: 0 5px;
		}

		.center-container {
			flex: 1;
			border-left: 1px solid var(--el-border-color-light);
			border-right: 1px solid var(--el-border-color-light);

			.center-header {
				height: 40px !important;
				border-bottom: 2px solid var(--el-border-color-light);
				margin: 0 5px;
			}

			.center-main {
				padding: 9px 0;
			}
		}

		.setting-container {
			width: 320px !important;
			padding: 0 5px;
		}
	}
</style>
